import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { api } from "../../services";
import {
  itemKey,
  partnerConnectReturnPath,
  partnerItemLogoApp,
  readPartnerSession,
  writePartnerSession,
} from "../../utils/partnerSession";
import { handlePlatformLogoError, resolveIntegrationLogo } from "../../utils/platformLogos";
import { getTabSenseAuthorizeUrl, getTabSenseRedirectUri } from "../../utils/tabSenseOAuth";

function ItemLogo({ item }) {
  const app = partnerItemLogoApp(item);
  const src = resolveIntegrationLogo(app) || item?.logo || "";
  if (!src) return null;
  return <img src={src} alt="" onError={(event) => handlePlatformLogoError(event, app)} />;
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}` };
}

function resolveStoreIdForBranch(stores, branchId) {
  const wanted = String(branchId || "");
  if (!wanted) return "";
  for (const store of stores || []) {
    const branches = store.branches || [];
    if (branches.some((b) => String(b._id) === wanted)) return String(store._id);
  }
  return stores?.[0]?._id ? String(stores[0]._id) : "";
}

function modeLabel(mode, channelKey, t) {
  if (mode === "oauth" || channelKey === "keeta") return t("partnerFlow.modeOauth", { app: "Keeta" });
  if (mode === "shop_sync") return t("partnerFlow.modeOauthGeneric");
  if (channelKey === "thechefz") return t("partnerFlow.modeChefz");
  if (channelKey === "ninja") return t("partnerFlow.modeNinja");
  if (mode === "addon_toggle") return t("partnerFlow.modeAddon");
  return t("partnerFlow.modeCredentials");
}

/**
 * Inline connect card for one partner-session item. Keeps the merchant on
 * /partner-sessions/:id — OAuth leaves only for the provider, then returns here.
 */
export default function PartnerConnectItem({
  sessionId,
  item,
  index,
  status,
  onStatus,
  busy,
  setBusy,
}) {
  const { t } = useTranslation("translation");
  const key = itemKey(item, index);
  const channelKey = String(item.channelKey || "").toLowerCase();
  const mode = String(item.connectionMode || "credentials");
  const isCreds = mode === "credentials" || channelKey === "jahez" || channelKey === "thechefz" || channelKey === "ninja";
  const isOauth = mode === "oauth" || mode === "shop_sync" || channelKey === "keeta" || channelKey === "foodics" || channelKey === "tabsense";
  const isAddon = mode === "addon_toggle" && !isCreds && !isOauth;

  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [form, setForm] = useState({ clientId: "", clientSecret: "", username: "", password: "" });
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState("");
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Tenant users must use /stores/my — GET /stores is admin-only (403).
        const res = await api.get("/stores/my", { headers: authHeaders() });
        if (cancelled) return;
        const list = res.data?.stores || res.data || [];
        setStores(Array.isArray(list) ? list : []);
        const resolved = resolveStoreIdForBranch(list, item.branchId);
        if (resolved) setStoreId(resolved);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.message || err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [item.branchId]);

  useEffect(() => {
    if (!storeId || !item.appId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/stores/${storeId}/apps`, { headers: authHeaders() });
        if (cancelled) return;
        const apps = res.data?.apps || res.data || [];
        const hit = (Array.isArray(apps) ? apps : []).find((app) => {
          const name = String(app.application || app.app || app.name || "").toLowerCase();
          return name.includes(channelKey) || String(app.appId) === String(item.appId);
        });
        if (hit) setAuthorized(true);
      } catch {
        /* store may not be connected yet */
      }
    })();
    return () => { cancelled = true; };
  }, [storeId, item.appId, channelKey]);

  useEffect(() => {
    if (!authorized || !storeId || channelKey !== "keeta") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/keeta-shops/store/${storeId}/all`, { headers: authHeaders() });
        if (cancelled) return;
        const list = res.data?.data || res.data?.shops || res.data || [];
        setShops(Array.isArray(list) ? list : []);
        if (Array.isArray(list) && list[0]) {
          setShopId(String(list[0]._id || list[0].keetaShopId || ""));
        }
      } catch {
        /* shops optional until after OAuth */
      }
    })();
    return () => { cancelled = true; };
  }, [authorized, storeId, channelKey]);

  const branchName = item.branchName || "—";
  const title = item.channelName || item.channelKey || t("partnerFlow.partnerFallback");

  const persistConnecting = () => {
    const prev = readPartnerSession() || {};
    writePartnerSession({
      ...prev,
      id: sessionId,
      step: "connect",
      connectingKey: key,
      branchId: item.branchId || null,
      branchName: item.branchName || null,
      channelName: item.channelKey || item.channelName,
      appId: item.appId || null,
    });
  };

  const startKeetaOAuth = () => {
    if (!storeId || !item.appId) {
      toast.error(t("partnerFlow.missingStoreOrApp"));
      return;
    }
    const applicationId = process.env.REACT_APP_KEETA_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_KEETA_REDIRECT_URI;
    if (!applicationId || !redirectUri) {
      toast.error(t("partnerFlow.oauthNotConfigured", { app: "Keeta" }));
      return;
    }
    persistConnecting();
    const state = JSON.stringify({
      storeId,
      appId: item.appId,
      partnerSession: sessionId,
      returnTo: partnerConnectReturnPath(sessionId),
    });
    const keetaOAuthUrl =
      `https://merchant.mykeeta.com/m/web/openapi/authorize` +
      `?responseType=authorization_code` +
      `&appId=${encodeURIComponent(applicationId)}` +
      `&redirectUri=${encodeURIComponent(redirectUri)}` +
      `&state=${encodeURIComponent(state)}` +
      `&scope=all`;
    window.location.assign(keetaOAuthUrl);
  };

  const startFoodicsOAuth = async () => {
    if (!storeId || !item.appId) {
      toast.error(t("partnerFlow.missingStoreOrApp"));
      return;
    }
    persistConnecting();
    try {
      const details = await api.get(`/available-apps/${item.appId}`, { headers: authHeaders() });
      const installUrl = details.data?.credentialsTemplate?.install_url
        || details.data?.app?.credentialsTemplate?.install_url
        || process.env.REACT_APP_FOODICS_INSTALL_URL;
      if (!installUrl) {
        toast.error(t("partnerFlow.oauthNotConfigured", { app: "Foodics" }));
        return;
      }
      window.location.assign(installUrl);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    }
  };

  const startTabSenseOAuth = async () => {
    if (!storeId || !item.appId) {
      toast.error(t("partnerFlow.missingStoreOrApp"));
      return;
    }
    persistConnecting();
    const clientId = process.env.REACT_APP_TABSENSE_CLIENT_ID;
    if (!clientId) {
      toast.error(t("partnerFlow.oauthNotConfigured", { app: "TabSense" }));
      return;
    }
    const oauthState = JSON.stringify({ storeId, appId: item.appId, partnerSession: sessionId });
    try {
      localStorage.setItem("tabsense_oauth_state", oauthState);
      localStorage.setItem(
        "tabsense_oauth_context",
        JSON.stringify({ appId: item.appId, storeId, partnerSession: sessionId })
      );
    } catch {
      /* ignore */
    }
    const url =
      `${getTabSenseAuthorizeUrl()}` +
      `?client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(getTabSenseRedirectUri())}` +
      `&response_type=code` +
      `&state=${encodeURIComponent(oauthState)}`;
    window.location.assign(url);
  };

  const startOauth = () => {
    if (channelKey === "foodics") return startFoodicsOAuth();
    if (channelKey === "tabsense") return startTabSenseOAuth();
    return startKeetaOAuth();
  };

  const saveCredentials = async () => {
    if (!storeId || !item.appId || !item.branchId) {
      toast.error(t("partnerFlow.missingStoreOrApp"));
      return;
    }
    setBusy(true);
    try {
      const credentials = channelKey === "thechefz"
        ? { username: form.username, password: form.password }
        : {
          client_id: form.clientId,
          client_secret: form.clientSecret,
        };
      await api.post("/apps", {
        appId: item.appId,
        storeId,
        branchId: item.branchId,
        integrationType: "STORE",
        credentials,
      }, { headers: authHeaders() });
      onStatus(key, "live");
      toast.success(t("partnerFlow.connected"));
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const enableAddon = async () => {
    if (!storeId || !item.appId || !item.branchId) {
      toast.error(t("partnerFlow.missingStoreOrApp"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/apps", {
        appId: item.appId,
        storeId,
        branchId: item.branchId,
        integrationType: "STORE",
        credentials: {},
      }, { headers: authHeaders() });
      onStatus(key, "live");
      toast.success(t("partnerFlow.connected"));
    } catch (err) {
      toast.error(err.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const mapKeetaShop = async () => {
    if (!item.branchId || !shopId) {
      toast.error(t("partnerFlow.pickShop"));
      return;
    }
    setBusy(true);
    try {
      const selected = shops.find((s) => String(s._id || s.keetaShopId) === String(shopId));
      await api.put(`/branches/${item.branchId}`, {
        keetaShopId: selected?.keetaShopId || shopId,
        selectedKeetaShopId: selected?._id || shopId,
      }, { headers: authHeaders() });
      onStatus(key, "live");
      toast.success(t("partnerFlow.connected"));
    } catch (err) {
      // Mapping endpoint shapes vary; if already authorized, allow mark live for partner session.
      if (authorized) {
        onStatus(key, "live");
        toast.success(t("partnerFlow.connected"));
      } else {
        toast.error(err.response?.data?.message || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const storeOptions = useMemo(
    () => (stores || []).map((store) => {
      const id = String(store._id);
      const name = typeof store.name === "string" ? store.name : (store.name?.en || store.name?.ar || store._id);
      const requested = (store.branches || []).some((b) => String(b._id) === String(item.branchId));
      return { id, name, requested };
    }),
    [stores, item.branchId]
  );

  const integrationId = item.branchId
    ? `LPS-${(channelKey || "LP").slice(0, 2).toUpperCase()}-${String(item.branchId).slice(-5).toUpperCase()}`
    : null;

  const head = (tag) => (
    <div className="psf-connect-head">
      <b>
        <ItemLogo item={item} />
        {title}
      </b>
      <span className="psf-connect-head-meta">
        <span className="psf-hint">{modeLabel(mode, channelKey, t)}</span>
        {tag}
      </span>
    </div>
  );

  const storePicker = status === "live" || status === "pending" ? (
    <div className="psf-store">
      <span className="lbl">{t("partnerFlow.store")}</span>
      <b>{storeOptions.find((s) => s.id === storeId)?.name || "—"}</b>
    </div>
  ) : (
    <div className="psf-store" role="group" aria-label={t("partnerFlow.store")}>
      <span className="lbl">{t("partnerFlow.store")}</span>
      {storeOptions.map((store) => (
        <button
          key={store.id}
          type="button"
          className={`choice${storeId === store.id ? " is-on" : ""}`}
          aria-pressed={storeId === store.id}
          onClick={() => setStoreId(store.id)}
        >
          {store.name}
          <small>
            {store.requested
              ? t("partnerFlow.requestedCount", { count: 1 })
              : t("partnerFlow.noneRequested")}
          </small>
        </button>
      ))}
    </div>
  );

  if (status === "live") {
    return (
      <div className="psf-connect-card is-live">
        {head(<span className="psf-tag add">{t("partnerFlow.status.live")}</span>)}
        {storePicker}
        <p className="psf-hint">
          {t("partnerFlow.connectedBranch", { branch: branchName, app: title })}
        </p>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="psf-connect-card">
        {head(<span className="psf-tag less">{t("partnerFlow.status.pending")}</span>)}
        {storePicker}
        <div className="psf-actions">
          <span className="psf-hint">{t("partnerFlow.paidFinishLater")}</span>
          <button type="button" className="psf-back-link" disabled={busy} onClick={() => onStatus(key, "to_connect")}>
            {t("partnerFlow.connectNow")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="psf-connect-card">
      {head(<span className="psf-tag">{t("partnerFlow.status.to_connect")}</span>)}
      {storePicker}

      {isCreds && channelKey !== "ninja" ? (
        <>
          <div className="psf-fields">
            {channelKey === "thechefz" ? (
              <>
                <label>
                  <span>{t("partnerFlow.username")}</span>
                  <input
                    value={form.username}
                    autoComplete="off"
                    onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                  />
                </label>
                <label>
                  <span>{t("partnerFlow.password")}</span>
                  <input
                    type="password"
                    value={form.password}
                    autoComplete="off"
                    onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  />
                </label>
              </>
            ) : (
              <>
                <label>
                  <span>{t("partnerFlow.clientId")}</span>
                  <input
                    value={form.clientId}
                    autoComplete="off"
                    onChange={(e) => setForm((prev) => ({ ...prev, clientId: e.target.value }))}
                  />
                </label>
                <label>
                  <span>{t("partnerFlow.clientSecret")}</span>
                  <input
                    type="password"
                    value={form.clientSecret}
                    autoComplete="off"
                    onChange={(e) => setForm((prev) => ({ ...prev, clientSecret: e.target.value }))}
                  />
                </label>
              </>
            )}
          </div>
          <p className="psf-hint">
            {channelKey === "thechefz" ? t("partnerFlow.chefzHint") : t("partnerFlow.jahezHint")}
          </p>
          {channelKey === "jahez" && integrationId ? (
            <div className="psf-map">
              <b>{branchName}</b>
              <span className="psf-hint">{t("partnerFlow.integrationId")}</span>
              <code>{integrationId}</code>
            </div>
          ) : null}
        </>
      ) : null}

      {channelKey === "ninja" ? (
        <>
          <p className="psf-lead" style={{ margin: 0 }}>{t("partnerFlow.ninjaHint")}</p>
          {integrationId ? (
            <div className="psf-map">
              <b>{branchName}</b>
              <span className="psf-mode-hint">{t("partnerFlow.integrationId")}</span>
              <code>{integrationId}</code>
            </div>
          ) : null}
        </>
      ) : null}

      {isOauth && authorized && channelKey === "keeta" && shops.length ? (
        <div className="psf-fields">
          <p className="psf-lead" style={{ margin: 0 }}>{t("partnerFlow.keetaAuthorized")}</p>
          <label>
            {t("partnerFlow.keetaShopFor", { branch: branchName })}
            <select value={shopId} onChange={(e) => setShopId(e.target.value)}>
              {shops.map((shop) => {
                const id = String(shop._id || shop.keetaShopId);
                const label = `${shop.keetaShopId || id} · ${shop.name || shop.shopName || ""}`;
                return <option key={id} value={id}>{label}</option>;
              })}
            </select>
          </label>
        </div>
      ) : null}

      {isOauth && !authorized ? (
        <p className="psf-lead" style={{ margin: 0 }}>{t("partnerFlow.oauthStaysOpen")}</p>
      ) : null}

      {isAddon ? (
        <p className="psf-lead" style={{ margin: 0 }}>{t("partnerFlow.addonHint")}</p>
      ) : null}

      <div className="psf-actions">
        <span />
        <span style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button type="button" className="psf-btn" disabled={busy} onClick={() => onStatus(key, "pending")}>
            {t("partnerFlow.connectLater")}
          </button>
          {isCreds && channelKey !== "ninja" ? (
            <button
              type="button"
              className="psf-btn primary"
              disabled={busy || !item.branchId || !storeId}
              onClick={saveCredentials}
            >
              {t("partnerFlow.connectNBranches", { count: 1 })}
            </button>
          ) : null}
          {channelKey === "ninja" ? (
            <button type="button" className="psf-btn primary" disabled={busy} onClick={() => onStatus(key, "live")}>
              {t("partnerFlow.markEnabled")}
            </button>
          ) : null}
          {isOauth && !authorized ? (
            <button type="button" className="psf-btn navy" disabled={busy || !storeId || !item.appId} onClick={startOauth}>
              {t("partnerFlow.signInWithApp", { app: title })}
            </button>
          ) : null}
          {isOauth && authorized ? (
            <button
              type="button"
              className="psf-btn primary"
              disabled={busy}
              onClick={channelKey === "keeta" ? mapKeetaShop : () => onStatus(key, "live")}
            >
              {t("partnerFlow.connectNBranches", { count: 1 })}
            </button>
          ) : null}
          {isAddon ? (
            <button type="button" className="psf-btn primary" disabled={busy || !storeId} onClick={enableAddon}>
              {t("partnerFlow.enableAddon")}
            </button>
          ) : null}
        </span>
      </div>
    </div>
  );
}
